
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewMerchantOrderLineModal from 'components/NewMerchantOrderLineModal';
import { formatDate } from 'utils/DateTimeConversion';

const formatUnitPrice = (amount, currency) => {
    if (amount == null || amount === '') return '-';
    const value = parseFloat(amount);
    if (Number.isNaN(value)) return '-';
    const cur = currency ? `${currency} ` : '';
    return `${cur}${value.toFixed(2)}`;
};

const MerchantOrderLines = () => {
    const [lines, setLines] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('merchant_order_lines');

    useEffect(() => {
        fetchLines();
    }, []);

    const fetchLines = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/merchant-order-lines/');
            setLines(response.data);
        } catch (error) {
            console.error('Error fetching merchant order lines:', error);
            setSnackbar({ open: true, message: 'Error loading merchant order lines', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveLine = async (payload) => {
        try {
            await axiosInstance.post('/merchant-order-lines/', payload);
            setSnackbar({ open: true, message: 'Merchant Order Line created successfully', severity: 'success' });
            fetchLines();
        } catch (error) {
            console.error('Error saving merchant order line:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/merchant-order-line/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Line Name",
            accessor: "name",
            width: "12%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Merchant",
            accessor: "merchant_id",
            width: "12%",
            Cell: ({ row }) => {
                const merchantId = row.original.merchant_id;
                const label = row.original.merchant?.name || merchantId || '-';
                if (!merchantId) return label;
                return (
                    <MDTypography
                        component="span"
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{ cursor: 'pointer' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/accounts/${merchantId}`);
                        }}
                    >
                        {label}
                    </MDTypography>
                );
            }
        },
        {
            Header: "Pax Count",
            accessor: "pax_count",
            width: "8%"
        },
        {
            Header: "Unit Price",
            accessor: "unit_price",
            width: "10%",
            Cell: ({ value, row }) => formatUnitPrice(value, row.original.currency)
        },
        {
            Header: "Service Start",
            accessor: "service_start",
            width: "10%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
        {
            Header: "Service End",
            accessor: "service_end",
            width: "10%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
        {
            Header: "Status",
            accessor: "status",
            width: "14%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Waiting For Merchant Acknowledgment'}
                    size="small"
                    color={
                        value === 'Booked' ? 'success' :
                        value === 'Cancelled' ? 'error' : 'default'
                    }
                />
            )
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "14%",
            Cell: ({ value }) => value ? formatDate(value, 'datetime') : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: lines,
    }), [lines]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Merchant Order Lines</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Order Line
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : lines.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        objectType="merchant_order_lines"
                                    />
                                ) : (
                                    <EmptyState moduleName="Merchant Order Line" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <NewMerchantOrderLineModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveLine}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantOrderLines;
